import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import { ArrowForwardIcon, LeftArrowThinIcon, RightArrowThinIcon } from 'images'

// Carousel-aware media area for Meta carousel placement cards — Facebook renders
// a horizontally-scrollable strip, Instagram renders one card at a time with
// chevron navigation (Instagram Feed also shows dot indicators).
//
// Reuses the existing circular white chevron button (`.button-scroll`) and dot
// indicators (`.instagram-carousel-dots`) from _campaign-preview.scss unchanged.
// Uses new `.preview-ads-carousel-*` rules for card sizing (not the v1
// `.carousel-preview-item` classes, which were tuned for wider v1 containers).
// _preview-ads-modal.scss instead.
const CarouselMedia = ({ platform, cards, mobileView = false, callToAction }) => {
  const trackRef = useRef(null)
  const [selectedCard, setSelectedCard] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const isFacebook = platform === 'facebook'

  const updateScrollState = useCallback(() => {
    if (!isFacebook || !trackRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    setCanScrollLeft(scrollLeft > 1)
    setCanScrollRight(scrollLeft < maxScrollLeft - 1)
  }, [isFacebook])

  useEffect(() => {
    if (!isFacebook) return undefined
    const frameId = window.requestAnimationFrame(updateScrollState)
    return () => window.cancelAnimationFrame(frameId)
  }, [cards, isFacebook, updateScrollState])

  if (!Array.isArray(cards) || !cards.length) return null

  const handleTrackScroll = (direction) => {
    if (!trackRef.current) return
    const scrollOffset = mobileView ? 200 : 320
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    const nextScrollLeft =
      direction === 'left'
        ? Math.max(0, scrollLeft - scrollOffset)
        : Math.min(maxScrollLeft, scrollLeft + scrollOffset)

    if (Math.abs(nextScrollLeft - scrollLeft) < 1) return
    trackRef.current.scrollTo({ left: nextScrollLeft, behavior: 'smooth' })
  }

  // `visibleByDefault` skips LazyLoadImage's IntersectionObserver gate:
  // until that observer fires, it renders a placeholder `<span>` carrying
  // `width`/`height` as *inline* styles (e.g. `height: 1080px`, the
  // fixture's intrinsic image size) — which beats the `aspect-ratio: 1/1`
  // card-sizing rule in _preview-ads-modal.scss and stretched the whole
  // card until some other interaction (e.g. scrolling) made the observer
  // re-evaluate and swap in the real, correctly-sized `<img>`. This modal
  // only ever shows a handful of carousel-card thumbnails at once, so
  // there's no real lazy-loading benefit to gate on in the first place.
  const renderMedia = (media, className) =>
    media?.type?.includes('video') ? (
      <video className={className} src={media.url} autoPlay loop muted playsInline />
    ) : (
      <LazyLoadImage
        className={className}
        alt={media?.filename}
        src={media?.url}
        width={media?.width}
        height={media?.height}
        visibleByDefault
      />
    )

  if (platform === 'reels') {
    const card = cards[selectedCard]
    return (
      <div className="carousel-preview-wrapper">
        {selectedCard > 0 && (
          <button
            type="button"
            className="button-scroll button-scroll-left"
            onClick={() => setSelectedCard((prev) => prev - 1)}
          >
            <LeftArrowThinIcon width={8} />
          </button>
        )}
        <div className="reels-carousel-preview">
          {card?.media?.type?.includes('video') ? (
            <video className="reel__video" src={card.media.url} autoPlay loop muted playsInline />
          ) : (
            <div
              className="reel__background"
              style={{ backgroundImage: `url(${card?.media?.url})` }}
            />
          )}
        </div>
        {cards.length > 1 && selectedCard < cards.length - 1 && (
          <button
            type="button"
            className="button-scroll button-scroll-right"
            onClick={() => setSelectedCard((prev) => prev + 1)}
          >
            <RightArrowThinIcon width={8} height={8} />
          </button>
        )}
      </div>
    )
  }

  if (platform === 'instagram') {
    const card = cards[selectedCard]
    return (
      <>
        <div className="carousel-preview-wrapper">
          {selectedCard > 0 && (
            <button
              type="button"
              className="button-scroll button-scroll-left"
              onClick={() => setSelectedCard((prev) => prev - 1)}
            >
              <LeftArrowThinIcon width={8} />
            </button>
          )}
          <div className="instagram__link">
            {renderMedia(card?.media, 'instagram__link_media')}
            <div className="instagram__link_order-now">
              <span>{callToAction}</span>
              <span>
                <ArrowForwardIcon />
              </span>
            </div>
          </div>
          {cards.length > 1 && selectedCard < cards.length - 1 && (
            <button
              type="button"
              className="button-scroll button-scroll-right"
              onClick={() => setSelectedCard((prev) => prev + 1)}
            >
              <RightArrowThinIcon width={8} height={8} />
            </button>
          )}
        </div>
        <div className="divider" />
        <div className="instagram-carousel-dots">
          {cards.map((dotCard, index) => (
            <div
              key={dotCard.id ?? index}
              className={`instagram-carousel-dot ${selectedCard === index ? 'active' : ''}`}
            />
          ))}
        </div>
      </>
    )
  }

  return (
    <div className="carousel-preview-wrapper">
      <button
        type="button"
        className="button-scroll button-scroll-left"
        onClick={() => handleTrackScroll('left')}
        disabled={!canScrollLeft}
      >
        <LeftArrowThinIcon width={8} />
      </button>
      <div className="preview-ads-carousel-track" ref={trackRef} onScroll={updateScrollState}>
        {cards.map((card, index) => {
          const displayUrl = mobileView ? card.url : card.url?.toUpperCase()
          return (
            <div key={card.id ?? index} className="preview-ads-carousel-item link">
              {renderMedia(card.media, 'media')}
              <div className="order-now">
                <div className="text d-flex flex-column gap-1">
                  <div className="url" title={card.url}>
                    {displayUrl}
                  </div>
                  <div className="headline">{card.headline}</div>
                </div>
                <div className="button">{callToAction}</div>
              </div>
            </div>
          )
        })}
      </div>
      <button
        type="button"
        className="button-scroll button-scroll-right"
        onClick={() => handleTrackScroll('right')}
        disabled={!canScrollRight}
      >
        <RightArrowThinIcon width={8} height={8} />
      </button>
    </div>
  )
}

CarouselMedia.propTypes = {
  platform: PropTypes.oneOf(['facebook', 'instagram', 'reels']).isRequired,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      media: PropTypes.object,
      headline: PropTypes.string,
      url: PropTypes.string,
    }),
  ).isRequired,
  mobileView: PropTypes.bool,
  callToAction: PropTypes.string,
}

export default CarouselMedia
